/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loadGuestStep, updateGuestStep, submitGuestBooking } from 'core/guestBooking/guestBookingApi';
import { StepData, VisitorRequest, VisitRequestFormState } from './types';
import {
    createGuardingRequest,
    deleteGuardingRequest,
    loadGuardingRequestForm,
    updateGuardingRequestForm,
    uploadGuardingRequestAttachment,
    deleteGuardingRequestAttachment,
    amendGuardingRequestAction,
} from './guardingRequestFormApi';

const emptyVisitRequestState: VisitorRequest = {
    encryptedId: null,
    id: null,
    site: null,
    requester: null,
    approver: null,
    costApproverByPass: false,
    attachments: [],
    status: '',
    currentStep: 0,
    visitors: null,
    dates: [],
    totalHours: 0,
    siteAccess: [],
    additionalComments: '',
    workTypeName: null,
    workTypes: null,
    workDetails: {
        description: null,
        workReference: null,
    },
    declaration: {
        signature: null,
        agreed: false,
    },
};

const initialState = {
    error: null,
    isFetching: false,
    isUpdating: false,
    isCreating: false,
    isAmending: false,
    isUploading: false,
    status: 0,
    visitRequest: emptyVisitRequestState,
    stepData: {
        sites: [],
        openDays: [],
        approvedVisits: [],
        totalHours: 0,
        enabled24_7: false,
    },
    applyToAllVisitors: true,
    lastRequestAmended: null,
    nsipSearchResults: [],
    isSearching: false,
} as VisitRequestFormState;

export const guardingRequestFormSlice = createSlice({
    name: 'guardingRequestForm',
    initialState,
    reducers: {
        setLastRequestAmended: (state, action) => {
            const { payload } = action;
            state.lastRequestAmended = payload;
        },
        updateVisitors: (state, action) => {
            const { payload } = action;
            state.visitRequest.visitors = payload;
        },
    },
    extraReducers: (builder) => {
        // Create
        builder
            .addCase(createGuardingRequest.pending, (state: VisitRequestFormState) => {
                state.isCreating = true;
                state.error = null;
            });
        builder
            .addCase(createGuardingRequest.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isCreating = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
                state.status = 200;
            });
        builder
            .addCase(createGuardingRequest.rejected, (
                state: VisitRequestFormState,
                action: PayloadAction<unknown>,
            ) => {
                state.isCreating = false;
                state.error = action.payload ?? null;
                state.visitRequest = emptyVisitRequestState;
            });

        // Load
        builder
            .addCase(loadGuardingRequestForm.pending, (state: VisitRequestFormState) => {
                state.isFetching = true;
                state.error = null;
            });
        builder
            .addCase(loadGuardingRequestForm.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isFetching = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
                state.status = 200;
            });
        builder
            .addCase(loadGuardingRequestForm.rejected, (
                state: VisitRequestFormState,
                action: PayloadAction<unknown>,
            ) => {
                state.isFetching = false;
                state.error = action.payload ?? null;
                state.visitRequest = emptyVisitRequestState;
            });

        // Update
        builder
            .addCase(updateGuardingRequestForm.pending, (state: VisitRequestFormState) => {
                state.isUpdating = true;
                state.error = null;
            });
        builder
            .addCase(updateGuardingRequestForm.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isUpdating = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
                state.status = 200;
            });
        builder
            .addCase(updateGuardingRequestForm.rejected, (
                state: VisitRequestFormState,
                action: PayloadAction<unknown>,
            ) => {
                state.isUpdating = false;
                state.error = action.payload ?? null;
            });

        // Upload attachment
        builder
            .addCase(uploadGuardingRequestAttachment.pending, (state: VisitRequestFormState) => {
                state.isFetching = true;
                state.error = null;
            });
        builder
            .addCase(uploadGuardingRequestAttachment.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ message: string, visitRequest: VisitorRequest }>,
            ) => {
                state.isFetching = false;
                state.visitRequest = action.payload.visitRequest;
                state.status = 200;
            });
        builder
            .addCase(uploadGuardingRequestAttachment.rejected, (
                state: VisitRequestFormState,
                action: PayloadAction<unknown>,
            ) => {
                state.isFetching = false;
                state.error = action.payload ?? null;
            });

        // Delete attachment
        builder
            .addCase(deleteGuardingRequestAttachment.pending, (state: VisitRequestFormState) => {
                state.isFetching = true;
                state.error = null;
            });
        builder
            .addCase(deleteGuardingRequestAttachment.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ message: string, visitRequest: VisitorRequest }>,
            ) => {
                state.isFetching = false;
                state.visitRequest = action.payload.visitRequest;
                state.status = 200;
            });
        builder
            .addCase(deleteGuardingRequestAttachment.rejected, (
                state: VisitRequestFormState,
                action: PayloadAction<unknown>,
            ) => {
                state.isFetching = false;
                state.error = action.payload ?? null;
            });

        // Amend action
        builder
            .addCase(amendGuardingRequestAction.pending, (state: VisitRequestFormState) => {
                state.isAmending = true;
                state.error = null;
            });
        builder
            .addCase(amendGuardingRequestAction.fulfilled, (
                state: VisitRequestFormState,
            ) => {
                state.isAmending = false;
                state.lastRequestAmended = null;
            });
        builder
            .addCase(amendGuardingRequestAction.rejected, (
                state: VisitRequestFormState,
                action: PayloadAction<unknown>,
            ) => {
                state.isAmending = false;
                state.error = action.payload ?? null;
            });

        // Delete
        builder
            .addCase(deleteGuardingRequest.pending, (_state: VisitRequestFormState) => {
                // Silent operation
            });
        builder
            .addCase(deleteGuardingRequest.fulfilled, (
                state: VisitRequestFormState,
            ) => {
                state.visitRequest = emptyVisitRequestState;
                state.stepData = initialState.stepData;
            });
        builder
            .addCase(deleteGuardingRequest.rejected, (
                _state: VisitRequestFormState,
            ) => {
                // Silent operation
            });
        // Guest booking (token-driven): a one-off link can raise this request
        // type, and the guest endpoints return the same {visitRequest, stepData}
        // shape, so the same store feeds the same step components.
        builder
            .addCase(loadGuestStep.pending, (state: VisitRequestFormState) => {
                state.isFetching = true;
                state.error = null;
            })
            .addCase(loadGuestStep.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isFetching = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
            })
            .addCase(loadGuestStep.rejected, (state: VisitRequestFormState, action: object) => {
                state.isFetching = false;
                state.error = action;
            });
        builder
            .addCase(updateGuestStep.pending, (state: VisitRequestFormState) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateGuestStep.fulfilled, (
                state: VisitRequestFormState,
                action: PayloadAction<{ visitRequest: VisitorRequest, stepData: StepData }>,
            ) => {
                state.isUpdating = false;
                state.visitRequest = action.payload.visitRequest;
                state.stepData = action.payload.stepData;
            })
            .addCase(updateGuestStep.rejected, (state: VisitRequestFormState, action: object) => {
                state.isUpdating = false;
                state.error = action;
            });
        builder
            .addCase(submitGuestBooking.pending, (state: VisitRequestFormState) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(submitGuestBooking.fulfilled, (state: VisitRequestFormState) => {
                state.isUpdating = false;
            })
            .addCase(submitGuestBooking.rejected, (state: VisitRequestFormState, action: object) => {
                state.isUpdating = false;
                state.error = action;
            });
    },
});

export const {
    setLastRequestAmended,
    updateVisitors,
} = guardingRequestFormSlice.actions;

export default guardingRequestFormSlice.reducer;
